import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Alert, Button, Modal, Form } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaKey } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchMainPrivileges,
    addPrivilege,
    updatePrivilege,
    deletePrivilege
} from '../redux/actions/PrivilegeAction';

const PrivilegeManagement = () => {
    const dispatch = useDispatch();
    const { loading: reduxLoading, error: reduxError } = useSelector(state => state.privilege || {});

    // Data states
    const [mainPrivileges, setMainPrivileges] = useState([]);
    const [selectedMainPrivilege, setSelectedMainPrivilege] = useState(null);
    const [selectedSubPrivilege, setSelectedSubPrivilege] = useState(null);
    const [selectedInternalPrivilege, setSelectedInternalPrivilege] = useState(null);

    // UI states
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [reminderBanner, setReminderBanner] = useState(null);

    // Modal states
    const [showAddMainModal, setShowAddMainModal] = useState(false);
    const [showEditMainModal, setShowEditMainModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAddSubModal, setShowAddSubModal] = useState(false);
    const [showEditSubModal, setShowEditSubModal] = useState(false);
    const [showAddInternalModal, setShowAddInternalModal] = useState(false);
    const [showEditInternalModal, setShowEditInternalModal] = useState(false);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [newPrivilegeName, setNewPrivilegeName] = useState('');

    // Form states
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        pathway: ''
    });
    const [editingItem, setEditingItem] = useState(null);
    const [deletingItem, setDeletingItem] = useState(null);
    const [parentForSub, setParentForSub] = useState(null);
    const [parentForInternal, setParentForInternal] = useState(null);

    // Fetch data on component mount
    useEffect(() => {
        fetchAllPrivileges();
    }, []);

    // Debug: Log mainPrivileges whenever it changes
    useEffect(() => {
        console.log('mainPrivileges state changed:', mainPrivileges);
        console.log('mainPrivileges length:', mainPrivileges.length);
    }, [mainPrivileges]);

    const fetchAllPrivileges = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const data = await dispatch(fetchMainPrivileges());
            
            // Ensure data is always an array
            const privilegesArray = Array.isArray(data) ? data : [];
            setMainPrivileges(privilegesArray);
            
            // Refresh selected items to show updated data
            if (selectedMainPrivilege) {
                const updatedMain = privilegesArray.find(p => p.id === selectedMainPrivilege.id);
                if (updatedMain) {
                    setSelectedMainPrivilege(updatedMain);
                    
                    // Update selected sub if exists
                    if (selectedSubPrivilege) {
                        const updatedSub = updatedMain.subprivilages?.find(s => s.id === selectedSubPrivilege.id);
                        setSelectedSubPrivilege(updatedSub || null);
                    }
                } else {
                    // Main was deleted, clear selections
                    setSelectedMainPrivilege(null);
                    setSelectedSubPrivilege(null);
                    setSelectedInternalPrivilege(null);
                }
            }
            
            setLoading(false);
        } catch (err) {
            console.error('Error fetching privileges:', err);
            setError(err.message || 'Failed to fetch privileges');
            setLoading(false);
        }
    };

    // CRUD Operations for Main Privileges
    const handleAddMainPrivilege = async () => {
        if (!formData.name.trim()) {
            alert('Name is required');
            return;
        }

        try {
            const privilegeData = {
                name: formData.name.trim(),
                description: formData.description.trim() || null,
                pathway: formData.pathway.trim() || null
            };

            await dispatch(addPrivilege(privilegeData));
            setSuccessMessage('Main privilege added successfully!');
            setNewPrivilegeName(privilegeData.name);
            setReminderBanner(`Don't forget to assign "${privilegeData.name}" to user roles in User Role Management!`);
            setShowAddMainModal(false);
            resetForm();
            fetchAllPrivileges();
            setShowReminderModal(true);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error adding main privilege:', err);
            alert('Failed to add main privilege: ' + (err.message || 'Unknown error'));
        }
    };

    const handleEditMainPrivilege = async () => {
        if (!formData.name.trim()) {
            alert('Name is required');
            return;
        }

        try {
            const updateData = {
                name: formData.name.trim(),
                description: formData.description.trim() || null,
                pathway: formData.pathway.trim() || null
            };

            await dispatch(updatePrivilege(editingItem.id, updateData));
            setSuccessMessage('Main privilege updated successfully!');
            setShowEditMainModal(false);
            resetForm();
            fetchAllPrivileges();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error updating main privilege:', err);
            alert('Failed to update main privilege: ' + (err.message || 'Unknown error'));
        }
    };

    // CRUD Operations for Sub-Privileges
    const handleAddSubPrivilege = async () => {
        if (!formData.name.trim()) {
            alert('Name is required');
            return;
        }

        if (!parentForSub) {
            alert('Parent privilege is not set');
            return;
        }

        try {
            const privilegeData = {
                name: formData.name.trim(),
                description: formData.description.trim() || null,
                pathway: formData.pathway.trim() || null,
                main_privilege: parentForSub.id
            };

            await dispatch(addPrivilege(privilegeData));
            setSuccessMessage('Sub-privilege added successfully!');
            setNewPrivilegeName(privilegeData.name);
            setReminderBanner(`Don't forget to assign "${privilegeData.name}" to user roles in User Role Management!`);
            setShowAddSubModal(false);
            resetForm();
            setParentForSub(null);
            fetchAllPrivileges();
            setShowReminderModal(true);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error adding sub-privilege:', err);
            alert('Failed to add sub-privilege: ' + (err.message || 'Unknown error'));
        }
    };

    const handleEditSubPrivilege = async () => {
        if (!formData.name.trim()) {
            alert('Name is required');
            return;
        }

        try {
            const updateData = {
                name: formData.name.trim(),
                description: formData.description.trim() || null,
                pathway: formData.pathway.trim() || null
            };

            await dispatch(updatePrivilege(editingItem.id, updateData));
            setSuccessMessage('Sub-privilege updated successfully!');
            setShowEditSubModal(false);
            resetForm();
            fetchAllPrivileges();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error updating sub-privilege:', err);
            alert('Failed to update sub-privilege: ' + (err.message || 'Unknown error'));
        }
    };

    // CRUD Operations for Internal Privileges
    const handleAddInternalPrivilege = async () => {
        if (!formData.name.trim()) {
            alert('Name is required');
            return;
        }

        if (!parentForInternal) {
            alert('Parent privilege is not set');
            return;
        }

        try {
            // Create the internal privilege with the parent's ID
            const privilegeData = {
                name: formData.name.trim(),
                description: formData.description.trim() || null,
                pathway: formData.pathway.trim() || null,
                main_privilege: parentForInternal.id  // Set to parent sub-privilege ID
            };

            console.log('Creating internal privilege with data:', privilegeData);
            const newPrivilege = await dispatch(addPrivilege(privilegeData));
            console.log('Created internal privilege:', newPrivilege);
            
            // Also link it to the parent through the many-to-many relationship
            const existingInternalIds = parentForInternal.internal_privileges?.map(ip => ip.id) || [];
            const updatedInternalIds = [...existingInternalIds, newPrivilege.id];
            
            console.log('Linking internal privilege to parent:', parentForInternal.id);
            console.log('Internal privilege IDs:', updatedInternalIds);
            
            await dispatch(updatePrivilege(parentForInternal.id, {
                internal_privileges_ids: updatedInternalIds
            }));

            setSuccessMessage('Internal privilege added successfully!');
            setNewPrivilegeName(privilegeData.name);
            setReminderBanner(`Don't forget to assign "${privilegeData.name}" to user roles in User Role Management!`);
            setShowAddInternalModal(false);
            resetForm();
            setParentForInternal(null);
            fetchAllPrivileges();
            setShowReminderModal(true);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error adding internal privilege:', err);
            alert('Failed to add internal privilege: ' + (err.message || 'Unknown error'));
        }
    };

    const handleEditInternalPrivilege = async () => {
        if (!formData.name.trim()) {
            alert('Name is required');
            return;
        }

        try {
            const updateData = {
                name: formData.name.trim(),
                description: formData.description.trim() || null,
                pathway: formData.pathway.trim() || null
            };

            await dispatch(updatePrivilege(editingItem.id, updateData));
            setSuccessMessage('Internal privilege updated successfully!');
            setShowEditInternalModal(false);
            resetForm();
            fetchAllPrivileges();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error updating internal privilege:', err);
            alert('Failed to update internal privilege: ' + (err.message || 'Unknown error'));
        }
    };

    const handleDelete = async () => {
        try {
            await dispatch(deletePrivilege(deletingItem.id));
            setSuccessMessage('Privilege deleted successfully!');
            setShowDeleteModal(false);
            setDeletingItem(null);
            fetchAllPrivileges();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error deleting privilege:', err);
            alert('Failed to delete privilege: ' + (err.message || 'Unknown error'));
        }
    };

    // Modal openers
    const openAddMainModal = () => {
        resetForm();
        setShowAddMainModal(true);
    };

    const openEditMainModal = (privilege) => {
        setEditingItem(privilege);
        setFormData({
            name: privilege.name,
            description: privilege.description || '',
            pathway: privilege.pathway || ''
        });
        setShowEditMainModal(true);
    };

    const openAddSubModal = (mainPrivilege) => {
        resetForm();
        setParentForSub(mainPrivilege);
        setShowAddSubModal(true);
    };

    const openEditSubModal = (subPrivilege) => {
        setEditingItem(subPrivilege);
        setFormData({
            name: subPrivilege.name,
            description: subPrivilege.description || '',
            pathway: subPrivilege.pathway || ''
        });
        setShowEditSubModal(true);
    };

    const openAddInternalModal = (parentPrivilege) => {
        resetForm();
        setParentForInternal(parentPrivilege);
        setShowAddInternalModal(true);
    };

    const openEditInternalModal = (internalPrivilege) => {
        setEditingItem(internalPrivilege);
        setFormData({
            name: internalPrivilege.name,
            description: internalPrivilege.description || '',
            pathway: internalPrivilege.pathway || ''
        });
        setShowEditInternalModal(true);
    };

    const openDeleteModal = (item, type) => {
        setDeletingItem({ ...item, type });
        setShowDeleteModal(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            pathway: ''
        });
        setEditingItem(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    if (loading) {
        return (
            <Container className="mt-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3">Loading privileges...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">
                    <Alert.Heading>Error Loading Data</Alert.Heading>
                    <p>{error}</p>
                    <hr />
                    <p className="mb-0">
                        Make sure the backend server is running and accessible.
                    </p>
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="p-4">
            <Row className="mb-4">
                <Col>
                    <h2><FaKey className="me-2" />Privilege Management</h2>
                    <p className="text-muted">Manage main privileges, sub-privileges, and internal privileges</p>
                </Col>
                <Col xs="auto">
                    <Button variant="secondary" onClick={fetchAllPrivileges} className="me-2">
                        🔄 Refresh
                    </Button>
                    {/* <Button variant="primary" onClick={openAddMainModal}>
                        <FaPlus className="me-2" />
                        Add Main Privilege
                    </Button> */}
                </Col>
            </Row>

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
                    {successMessage}
                </Alert>
            )}

            {reminderBanner && (
                <Alert variant="info" dismissible onClose={() => setReminderBanner(null)} className="d-flex align-items-center">
                    <span className="me-2">🔔</span>
                    <div className="flex-grow-1">
                        <strong>Reminder:</strong> {reminderBanner}
                    </div>
                </Alert>
            )}

            {/* Dropdown Selection Section */}
            <Row className="mb-4">
                <Col md={4}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Main Privileges</h6>
                            <Button variant="primary" size="sm" onClick={openAddMainModal}>
                                <FaPlus />
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            {mainPrivileges.length === 0 ? (
                                <Alert variant="info" className="mb-0">
                                    No main privileges found.
                                </Alert>
                            ) : (
                                <Form.Select 
                                    value={selectedMainPrivilege?.id || ''}
                                    onChange={(e) => {
                                        const selected = mainPrivileges.find(p => p.id === parseInt(e.target.value));
                                        setSelectedMainPrivilege(selected || null);
                                        setSelectedSubPrivilege(null);
                                        setSelectedInternalPrivilege(null);
                                    }}
                                    size="lg"
                                >
                                    <option value="">-- Select Main Privilege --</option>
                                    {mainPrivileges.map(mainPriv => (
                                        <option key={mainPriv.id} value={mainPriv.id}>
                                            {mainPriv.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={4}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Sub-Privileges</h6>
                            {selectedMainPrivilege && (
                                <Button 
                                    variant="info" 
                                    size="sm" 
                                    onClick={() => openAddSubModal(selectedMainPrivilege)}
                                >
                                    <FaPlus />
                                </Button>
                            )}
                        </Card.Header>
                        <Card.Body>
                            {!selectedMainPrivilege ? (
                                <Alert variant="light" className="mb-0">
                                    Select a main privilege first
                                </Alert>
                            ) : !selectedMainPrivilege.subprivilages || selectedMainPrivilege.subprivilages.length === 0 ? (
                                <Alert variant="light" className="mb-0">
                                    No sub-privileges available
                                </Alert>
                            ) : (
                                <Form.Select 
                                    value={selectedSubPrivilege?.id || ''}
                                    onChange={(e) => {
                                        const selected = selectedMainPrivilege.subprivilages.find(p => p.id === parseInt(e.target.value));
                                        setSelectedSubPrivilege(selected || null);
                                        setSelectedInternalPrivilege(null);
                                    }}
                                    size="lg"
                                >
                                    <option value="">-- Select Sub-Privilege --</option>
                                    {selectedMainPrivilege.subprivilages.map(subPriv => (
                                        <option key={subPriv.id} value={subPriv.id}>
                                            {subPriv.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={4}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Internal Privileges</h6>
                            {(selectedMainPrivilege || selectedSubPrivilege) && (
                                <Button 
                                    variant="warning" 
                                    size="sm" 
                                    onClick={() => openAddInternalModal(selectedSubPrivilege || selectedMainPrivilege)}
                                >
                                    <FaPlus />
                                </Button>
                            )}
                        </Card.Header>
                        <Card.Body>
                            {!selectedMainPrivilege && !selectedSubPrivilege ? (
                                <Alert variant="light" className="mb-0">
                                    Select a privilege first
                                </Alert>
                            ) : (() => {
                                const parent = selectedSubPrivilege || selectedMainPrivilege;
                                const internals = parent?.internal_privileges || [];
                                return internals.length === 0 ? (
                                    <Alert variant="light" className="mb-0">
                                        No internal privileges available
                                    </Alert>
                                ) : (
                                    <Form.Select 
                                        value={selectedInternalPrivilege?.id || ''}
                                        onChange={(e) => {
                                            const selected = internals.find(p => p.id === parseInt(e.target.value));
                                            setSelectedInternalPrivilege(selected || null);
                                        }}
                                        size="lg"
                                    >
                                        <option value="">-- Select Internal Privilege --</option>
                                        {internals.map(internalPriv => (
                                            <option key={internalPriv.id} value={internalPriv.id}>
                                                {internalPriv.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                );
                            })()}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Details Section */}
            <Row>
                <Col>
                    <Card>
                        <Card.Header>
                            <h5>Privilege Details</h5>
                        </Card.Header>
                        <Card.Body>
                            {!selectedMainPrivilege && !selectedSubPrivilege && !selectedInternalPrivilege ? (
                                <Alert variant="info">
                                    Select a privilege from the dropdowns above to view and manage its details.
                                </Alert>
                            ) : (
                                (() => {
                                    const currentPrivilege = selectedInternalPrivilege || selectedSubPrivilege || selectedMainPrivilege;
                                    const privilegeType = selectedInternalPrivilege ? 'Internal' : 
                                                         selectedSubPrivilege ? 'Sub' : 'Main';
                                    
                                    return (
                                        <div>
                                            <div className="mb-4">
                                                <h4>
                                                    <Badge bg={
                                                        privilegeType === 'Internal' ? 'warning' : 
                                                        privilegeType === 'Sub' ? 'info' : 'primary'
                                                    }>
                                                        {privilegeType} Privilege
                                                    </Badge>
                                                    <span className="ms-3">{currentPrivilege.name}</span>
                                                </h4>
                                            </div>

                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <p><strong>Name:</strong> {currentPrivilege.name}</p>
                                                </Col>
                                                <Col md={6}>
                                                    <p><strong>Pathway:</strong> {currentPrivilege.pathway || 'N/A'}</p>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col>
                                                    <p><strong>Description:</strong></p>
                                                    <p className="text-muted">{currentPrivilege.description || 'No description provided'}</p>
                                                </Col>
                                            </Row>

                                            <div className="mt-4">
                                                {privilegeType === 'Main' && (
                                                    <>
                                                        <Button 
                                                            variant="warning" 
                                                            className="me-2"
                                                            onClick={() => openEditMainModal(currentPrivilege)}
                                                        >
                                                            <FaEdit className="me-1" /> Edit
                                                        </Button>
                                                        <Button 
                                                            variant="danger"
                                                            onClick={() => openDeleteModal(currentPrivilege, 'main')}
                                                        >
                                                            <FaTrash className="me-1" /> Delete
                                                        </Button>
                                                    </>
                                                )}
                                                {privilegeType === 'Sub' && (
                                                    <>
                                                        <Button 
                                                            variant="warning" 
                                                            className="me-2"
                                                            onClick={() => openEditSubModal(currentPrivilege)}
                                                        >
                                                            <FaEdit className="me-1" /> Edit
                                                        </Button>
                                                        <Button 
                                                            variant="danger"
                                                            onClick={() => openDeleteModal(currentPrivilege, 'sub')}
                                                        >
                                                            <FaTrash className="me-1" /> Delete
                                                        </Button>
                                                    </>
                                                )}
                                                {privilegeType === 'Internal' && (
                                                    <>
                                                        <Button 
                                                            variant="warning" 
                                                            className="me-2"
                                                            onClick={() => openEditInternalModal(currentPrivilege)}
                                                        >
                                                            <FaEdit className="me-1" /> Edit
                                                        </Button>
                                                        <Button 
                                                            variant="danger"
                                                            onClick={() => openDeleteModal(currentPrivilege, 'internal')}
                                                        >
                                                            <FaTrash className="me-1" /> Delete
                                                        </Button>
                                                    </>
                                                )}
                                            </div>

                                            {/* Show Sub-Privileges count for Main */}
                                            {privilegeType === 'Main' && currentPrivilege.subprivilages && (
                                                <div className="mt-4 pt-3 border-top">
                                                    <p>
                                                        <Badge bg="info">{currentPrivilege.subprivilages.length}</Badge>
                                                        {' '}Sub-Privilege{currentPrivilege.subprivilages.length !== 1 ? 's' : ''}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Show Internal Privileges count */}
                                            {currentPrivilege.internal_privileges && currentPrivilege.internal_privileges.length > 0 && (
                                                <div className="mt-4 pt-3 border-top">
                                                    <p>
                                                        <Badge bg="warning">{currentPrivilege.internal_privileges.length}</Badge>
                                                        {' '}Internal Privilege{currentPrivilege.internal_privileges.length !== 1 ? 's' : ''}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Add Main Privilege Modal */}
            <Modal show={showAddMainModal} onHide={() => setShowAddMainModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Add New Main Privilege</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name *</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="e.g., User Management"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Brief description of this privilege"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Pathway</Form.Label>
                            <Form.Control
                                type="text"
                                name="pathway"
                                value={formData.pathway}
                                onChange={handleInputChange}
                                placeholder="e.g., /admin/users"
                            />
                            <Form.Text className="text-muted">
                                Frontend route or navigation key
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddMainModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleAddMainPrivilege}>
                        Add Privilege
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Main Privilege Modal */}
            <Modal show={showEditMainModal} onHide={() => setShowEditMainModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Main Privilege</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name *</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Pathway</Form.Label>
                            <Form.Control
                                type="text"
                                name="pathway"
                                value={formData.pathway}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditMainModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleEditMainPrivilege}>
                        Update Privilege
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Sub-Privilege Modal */}
            <Modal show={showAddSubModal} onHide={() => setShowAddSubModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Add Sub-Privilege to "{parentForSub?.name}"</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name *</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="e.g., View Users"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Pathway</Form.Label>
                            <Form.Control
                                type="text"
                                name="pathway"
                                value={formData.pathway}
                                onChange={handleInputChange}
                                placeholder="e.g., /admin/users/view"
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddSubModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="info" onClick={handleAddSubPrivilege}>
                        Add Sub-Privilege
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Sub-Privilege Modal */}
            <Modal show={showEditSubModal} onHide={() => setShowEditSubModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Sub-Privilege</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name *</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Pathway</Form.Label>
                            <Form.Control
                                type="text"
                                name="pathway"
                                value={formData.pathway}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditSubModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="info" onClick={handleEditSubPrivilege}>
                        Update Sub-Privilege
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Internal Privilege Modal */}
            <Modal show={showAddInternalModal} onHide={() => setShowAddInternalModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Add Internal Privilege to "{parentForInternal?.name}"</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name *</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="e.g., Delete User Action"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Pathway</Form.Label>
                            <Form.Control
                                type="text"
                                name="pathway"
                                value={formData.pathway}
                                onChange={handleInputChange}
                                placeholder="e.g., user.delete"
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddInternalModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="warning" onClick={handleAddInternalPrivilege}>
                        Add Internal Privilege
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Internal Privilege Modal */}
            <Modal show={showEditInternalModal} onHide={() => setShowEditInternalModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Internal Privilege</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name *</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Pathway</Form.Label>
                            <Form.Control
                                type="text"
                                name="pathway"
                                value={formData.pathway}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditInternalModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="warning" onClick={handleEditInternalPrivilege}>
                        Update Internal Privilege
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete the {deletingItem?.type} privilege <strong>"{deletingItem?.name}"</strong>?
                    <br />
                    <br />
                    {deletingItem?.type === 'main' && (
                        <Alert variant="warning">
                            <strong>Warning:</strong> Deleting a main privilege will also delete all its sub-privileges and internal privileges.
                        </Alert>
                    )}
                    This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Reminder Modal */}
            <Modal show={showReminderModal} onHide={() => setShowReminderModal(false)} centered>
                <Modal.Header closeButton style={{ backgroundColor: '#d1ecf1', borderBottom: '1px solid #bee5eb' }}>
                    <Modal.Title style={{ color: '#0c5460' }}>
                        🔔 Important Reminder
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info" className="mb-3">
                        <strong>Privilege Created Successfully!</strong>
                        <br />
                        <span className="text-muted">Privilege: <strong>{newPrivilegeName}</strong></span>
                    </Alert>
                    <p className="mb-2">
                        <strong>Next Step:</strong> Don't forget to assign this privilege to user roles!
                    </p>
                    <p className="text-muted">
                        Go to <strong>User Role Management</strong> to assign the newly created privilege to the appropriate user roles.
                        Without this step, users won't have access to this privilege.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowReminderModal(false)}>
                        Got it!
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default PrivilegeManagement;
